import { unwrap, type Schemas } from '@mrigl/api-client';
import { Alert, Button, Card, SelectField, TextAreaField } from '@mrigl/ui';
import { useCallback, useEffect, useState, type SubmitEvent } from 'react';
import { api, errorMessage } from '../../api.js';
import { useI18n } from '../../i18n.js';

type Order = Schemas['Order'];
type Risk = Schemas['CodRisk'];
type Outcome = Schemas['LogCallRequest']['outcome'];

const OUTCOMES: readonly Outcome[] = [
  'no_answer',
  'confirmed',
  'declined',
  'callback',
  'unreachable',
];

/**
 * Cash-on-delivery follow-up on the order page: this store's history for the number, the
 * calls so far, a call log form ("confirmed" confirms, "declined" cancels) and blocking.
 */
export function CodPanel({
  storeId,
  order,
  canManage,
  onChange,
}: {
  readonly storeId: string;
  readonly order: Order;
  readonly canManage: boolean;
  readonly onChange: (order: Order) => Promise<void>;
}) {
  const { t } = useI18n();
  const orderId = order.orderId;
  const [risk, setRisk] = useState<Risk | null>(null);
  const [outcome, setOutcome] = useState<Outcome>('no_answer');
  const [note, setNote] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setRisk(
      await unwrap(
        api.GET('/admin-api/v1/stores/{storeId}/orders/{orderId}/cod-risk', {
          params: { path: { storeId, orderId } },
        }),
      ),
    );
  }, [storeId, orderId]);

  useEffect(() => {
    load().catch((failure: unknown) => {
      setError(errorMessage(t, failure));
    });
  }, [load, t]);

  async function logCall(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      const next = await unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/orders/{orderId}/calls', {
          params: { path: { storeId, orderId } },
          body: {
            outcome,
            note: note.trim() === '' ? null : note.trim(),
            version: order.version,
          },
        }),
      );
      setNote('');
      setNotice(t('admin.cod.callLogged'));
      await onChange(next);
    } catch (failure) {
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  }

  async function toggleBlock() {
    if (risk === null) return;
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      if (risk.blocked) {
        await unwrap(
          api.DELETE('/admin-api/v1/stores/{storeId}/orders/{orderId}/cod-block', {
            params: { path: { storeId, orderId } },
          }),
        );
        setNotice(t('admin.cod.unblockedDone'));
      } else {
        await unwrap(
          api.POST('/admin-api/v1/stores/{storeId}/cod-blocked-phones', {
            params: { path: { storeId } },
            body: { orderId, reason: null },
          }),
        );
        setNotice(t('admin.cod.blockedDone'));
      }
      await load();
    } catch (failure) {
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  }

  const open = order.status === 'placed' || order.status === 'confirmed';
  const history = risk?.history;
  return (
    <Card labelledBy="order-cod">
      <h2 id="order-cod" className="mrigl-subheading">
        {t('admin.cod.panel')}
      </h2>
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {risk === null || history === undefined ? null : (
        <>
          {risk.warning ? (
            <Alert tone="error">{t('admin.cod.warning', { returned: history.returned })}</Alert>
          ) : null}
          {risk.blocked ? <p>{t('admin.cod.isBlocked')}</p> : null}
          <p>
            {history.orders === 0
              ? t('admin.cod.firstOrder')
              : t('admin.cod.pastOrders', {
                  delivered: String(history.delivered),
                  returned: String(history.returned),
                  cancelled: String(history.cancelled),
                  inProgress: String(history.inProgress),
                })}
          </p>
          <p className="mrigl-muted">{t('admin.cod.calls', { count: risk.calls.attempts })}</p>
        </>
      )}
      {canManage && open ? (
        <form
          className="mrigl-stack"
          aria-label={t('admin.cod.logCall')}
          onSubmit={(event) => void logCall(event)}
        >
          <p className="mrigl-muted">{t('admin.cod.outcomeHint')}</p>
          <SelectField
            label={t('admin.cod.outcome')}
            value={outcome}
            onChange={(event) => {
              setOutcome(event.target.value as Outcome);
            }}
            options={OUTCOMES.map((value) => ({
              value,
              label: t(`admin.cod.outcome.${value}`),
            }))}
          />
          <TextAreaField
            label={t('admin.cod.callNote')}
            maxLength={1000}
            dir="auto"
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
          <Button type="submit" busy={busy}>
            {t('admin.cod.logCall')}
          </Button>
        </form>
      ) : null}
      {canManage && risk !== null ? (
        <Button variant="secondary" busy={busy} onClick={() => void toggleBlock()}>
          {risk.blocked ? t('admin.cod.unblock') : t('admin.cod.block')}
        </Button>
      ) : null}
    </Card>
  );
}
